import { splitMsgId } from "./logMessage";

/** One attribute of a V1 log line: a row of the expanded row's Attributes. */
export interface LogAttribute {
  /** Search Log field id, lower-case: `msgid`, `iteration_id`, `method`. */
  key: string;
  /** What the panel and the Search Log chip show. */
  label: string;
  value: string;
}

// The block that follows the MSGID and repeats it:
// `[MSGID=EXECUTION_STARTED_INF Iteration_ID=ceaa41be44d4 Ident=BGP Direction=IN]`.
// V1 values (staging, CTX-1356 discovery) never have a space or quotes. The V2
// format the docs describe quotes them — `[MSGID="NAME" Ident="A B"]` — and a
// quoted value may have spaces; the quotes are not part of the value.
const ATTRIBUTE_BLOCK = /^\[([^\]]*)\]/;
const BLOCK_PAIR = /([^\s=]+)=(?:"([^"]*)"|(\S+))/g;

// `10.228.172.121:53154 - GET /heartbeat HTTP/1.1 200`. Staging only shows
// `GET /heartbeat`, so any method, path and status is accepted.
const HTTP_ACCESS = /^(\S+:\d+)\s+-\s+([A-Z]+)\s+(\S+)\s+(HTTP\/[\d.]+)\s+(\d{3})\b/;

/**
 * Pairs of the `[MSGID=… key=value …]` block, after the MSGID itself. The
 * `(60_024)` code and the body (text or a Python dict) are not attributes.
 */
function msgIdAttributes(msgId: string, rest: string): LogAttribute[] {
  const attributes: LogAttribute[] = [{ key: "msgid", label: "MSGID", value: msgId }];
  const block = rest.match(ATTRIBUTE_BLOCK)?.[1] ?? "";
  for (const [, label, quoted, bare] of Array.from(block.matchAll(BLOCK_PAIR))) {
    const key = label.toLowerCase();
    // First one wins, which also drops the block's copy of the MSGID.
    if (attributes.some((attribute) => attribute.key === key)) continue;
    attributes.push({ key, label, value: quoted ?? bare });
  }
  return attributes;
}

function httpAccessAttributes(text: string): LogAttribute[] {
  const match = text.match(HTTP_ACCESS);
  if (!match) return [];
  const [, origin, method, path, protocol, status] = match;
  return [
    { key: "origin", label: "Origin", value: origin },
    { key: "method", label: "Method", value: method },
    { key: "path", label: "Path", value: path },
    { key: "protocol", label: "Protocol", value: protocol },
    { key: "status", label: "Status", value: status },
  ];
}

/**
 * The attributes a V1 log message carries, for the expanded row and for Search
 * Log. The two formats staging emits are recognised — a MSGID with its
 * `[…]` block, and an HTTP access line; anything else has none. Never throws.
 */
export function parseLogAttributes(message: string): LogAttribute[] {
  const { msgId, rest } = splitMsgId(message);
  return msgId ? msgIdAttributes(msgId, rest) : httpAccessAttributes(rest);
}

/** One attribute's value in a message, or `undefined` when it has none. */
export function logAttributeValue(message: string, key: string): string | undefined {
  return parseLogAttributes(message).find((attribute) => attribute.key === key)?.value;
}
